import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { Package, Plus, Globe, X, Pencil, Check } from 'lucide-react'

import { apiFetch } from '@/lib/api'
import type { Product, ProductWithAssets } from '@/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------
async function fetchProducts(): Promise<ProductWithAssets[]> {
  const res = await apiFetch('/api/v1/products?limit=500')
  if (!res.ok) throw new Error('Failed to load products')
  const data = await res.json()
  return data.items ?? data
}

async function fetchProduct(id: number): Promise<ProductWithAssets> {
  const res = await apiFetch(`/api/v1/products/${id}`)
  if (!res.ok) throw new Error('Failed to load product')
  return res.json()
}

async function createProduct(body: Record<string, unknown>): Promise<Product> {
  const res = await apiFetch('/api/v1/products', {
    method: 'POST',
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error('Failed to create product')
  return res.json()
}

async function updateProduct(id: number, body: Record<string, unknown>): Promise<Product> {
  const res = await apiFetch(`/api/v1/products/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error('Failed to update product')
  return res.json()
}

async function deleteProduct(id: number): Promise<void> {
  const res = await apiFetch(`/api/v1/products/${id}`, { method: 'DELETE' })
  if (!res.ok) throw new Error('Failed to delete product')
}

async function linkAssetToProduct(
  productId: number,
  assetId: number,
  role: string,
): Promise<ProductWithAssets> {
  const res = await apiFetch(`/api/v1/products/${productId}/assets`, {
    method: 'POST',
    body: JSON.stringify({ asset_id: assetId, role }),
  })
  if (!res.ok) throw new Error('Failed to link image to product')
  return res.json()
}

async function unlinkAssetFromProduct(productId: number, assetId: number): Promise<void> {
  const res = await apiFetch(`/api/v1/products/${productId}/assets/${assetId}`, {
    method: 'DELETE',
  })
  if (!res.ok) throw new Error('Failed to unlink image from product')
}

async function quickSaveAsset(file: File): Promise<{ id: number }> {
  const fd = new FormData()
  fd.append('file', file)
  const res = await apiFetch('/api/v1/assets/quick-save', { method: 'POST', body: fd })
  if (!res.ok) throw new Error('Image upload failed')
  return res.json()
}

async function scrapeProduct(url: string): Promise<Product> {
  const res = await apiFetch('/api/v1/products/scrape', {
    method: 'POST',
    body: JSON.stringify({ url }),
  })
  if (!res.ok) throw new Error('Failed to scrape URL')
  return res.json()
}

// ---------------------------------------------------------------------------
// ProductAssetManager — add / remove linked images per product. Used inside
// the product edit dialog so users can build up multi-angle galleries (front,
// 3-4, label close-up, lifestyle) for the Create-Ad auto-bind to consume.
// ---------------------------------------------------------------------------
const PRODUCT_ROLES: Array<{ value: string; label: string }> = [
  { value: 'hero_image', label: 'Hero (front)' },
  { value: 'lifestyle', label: 'Lifestyle' },
  { value: 'texture', label: 'Texture / detail' },
  { value: 'packaging', label: 'Packaging' },
]

function ProductAssetManager({
  product,
  onChanged,
}: {
  product: ProductWithAssets
  onChanged: (next: ProductWithAssets) => void
}) {
  const [uploading, setUploading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [pendingRole, setPendingRole] = React.useState<string>('hero_image')
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const handleUploadAndLink = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    setError(null)
    try {
      const asset = await quickSaveAsset(file)
      const updated = await linkAssetToProduct(product.id, asset.id, pendingRole)
      onChanged(updated)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleUnlink = async (assetId: number) => {
    setError(null)
    try {
      await unlinkAssetFromProduct(product.id, assetId)
      onChanged({
        ...product,
        assets: (product.assets ?? []).filter((pa) => pa.asset_id !== assetId),
      })
    } catch (err) {
      setError((err as Error).message)
    }
  }

  return (
    <div className="space-y-2">
      {(product.assets?.length ?? 0) > 0 ? (
        <div className="flex flex-wrap gap-2">
          {product.assets!.map((pa) => (
            <div key={pa.id} className="relative group">
              <img
                src={`/uploads/${pa.asset.file_path}`}
                alt={pa.asset.original_filename ?? pa.asset.filename}
                className="h-20 w-20 object-cover rounded-md border border-border"
              />
              <span className="absolute bottom-0 left-0 right-0 text-[10px] text-center bg-black/60 text-white rounded-b-md py-0.5 truncate px-1">
                {pa.role}
              </span>
              <button
                type="button"
                onClick={() => handleUnlink(pa.asset_id)}
                className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-background border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive hover:text-destructive-foreground"
                aria-label="Unlink image"
                title="Unlink image"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          No images yet. Add product photos and they'll auto-attach when this product is selected in Create Ad.
        </p>
      )}

      <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-border">
        <label className="text-xs text-muted-foreground">Add as:</label>
        <select
          value={pendingRole}
          onChange={(e) => setPendingRole(e.target.value)}
          className="text-xs rounded-md border border-border bg-background px-2 py-1"
          disabled={uploading}
        >
          {PRODUCT_ROLES.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? 'Uploading…' : (
            <>
              <Plus className="h-3.5 w-3.5" />
              Add image
            </>
          )}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="sr-only"
          onChange={handleUploadAndLink}
          disabled={uploading}
        />
        <p className="text-[11px] text-muted-foreground">
          Front, 3-4 angle, label close-up — more angles = better Seedance accuracy.
        </p>
      </div>

      {error && (
        <p className="text-xs text-destructive">{error}</p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// TagList — inline editable pill list
// ---------------------------------------------------------------------------
function TagList({
  tags,
  onChange,
}: {
  tags: string[]
  onChange: (next: string[]) => void
}) {
  const [draft, setDraft] = React.useState('')

  const add = () => {
    const val = draft.trim()
    if (val && !tags.includes(val)) onChange([...tags, val])
    setDraft('')
  }

  return (
    <div className="flex flex-wrap gap-1.5 items-center">
      {tags.map((t) => (
        <span
          key={t}
          className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-accent border border-border text-foreground"
        >
          {t}
          <button
            type="button"
            onClick={() => onChange(tags.filter((x) => x !== t))}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label={`Remove ${t}`}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        className="text-xs bg-transparent border-b border-border outline-none w-28 text-foreground placeholder:text-muted-foreground"
        placeholder="Add tag…"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); add() }
        }}
        onBlur={add}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// ProductDetailModal
// ---------------------------------------------------------------------------
function ProductDetailModal({
  productId,
  onClose,
  onSaved,
}: {
  productId: number
  onClose: () => void
  onSaved: () => void
}) {
  const navigate = useNavigate()
  const [product, setProduct] = React.useState<ProductWithAssets | null>(null)
  const [saving, setSaving] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const [draft, setDraft] = React.useState<Partial<ProductWithAssets>>({})

  React.useEffect(() => {
    fetchProduct(productId).then((p) => {
      setProduct(p)
      setDraft(p)
    })
  }, [productId])

  const set = <K extends keyof ProductWithAssets>(key: K, value: ProductWithAssets[K]) =>
    setDraft((d) => ({ ...d, [key]: value }))

  const save = async () => {
    if (!product) return
    setSaving(true)
    try {
      await updateProduct(product.id, draft as Record<string, unknown>)
      onSaved()
      onClose()
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!product) return
    setDeleting(true)
    try {
      await deleteProduct(product.id)
      onSaved()
      onClose()
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        {!product ? (
          <div className="space-y-3 py-4">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">{product.name}</DialogTitle>
            </DialogHeader>

            <div className="space-y-5 py-2">
              {/* Name */}
              <Field label="Name">
                <Input
                  value={draft.name ?? ''}
                  onChange={(e) => set('name', e.target.value)}
                />
              </Field>

              {/* Description */}
              <Field label="Description">
                <Textarea
                  rows={3}
                  value={draft.description ?? ''}
                  onChange={(e) => set('description', e.target.value)}
                  className="resize-none"
                />
              </Field>

              {/* Price + Category */}
              <div className="grid grid-cols-2 gap-4">
                <Field label="Price">
                  <Input
                    value={draft.price ?? ''}
                    onChange={(e) => set('price', e.target.value)}
                  />
                </Field>
                <Field label="Category">
                  <Input
                    value={draft.category ?? ''}
                    onChange={(e) => set('category', e.target.value)}
                  />
                </Field>
              </div>

              {/* Benefits */}
              <Field label="Benefits">
                <TagList
                  tags={draft.benefits ?? []}
                  onChange={(v) => set('benefits', v)}
                />
              </Field>

              {/* Ingredients */}
              <Field label="Ingredients">
                <TagList
                  tags={draft.ingredients ?? []}
                  onChange={(v) => set('ingredients', v)}
                />
              </Field>

              {/* Brand Voice */}
              <Field label="Brand Voice">
                <Input
                  value={draft.brand_voice ?? ''}
                  onChange={(e) => set('brand_voice', e.target.value)}
                />
              </Field>

              {/* Target Audience */}
              <Field label="Target Audience">
                <Input
                  value={draft.target_audience ?? ''}
                  onChange={(e) => set('target_audience', e.target.value)}
                />
              </Field>

              {/* Usage Tips */}
              <Field label="Usage Tips">
                <Textarea
                  rows={2}
                  value={draft.usage_tips ?? ''}
                  onChange={(e) => set('usage_tips', e.target.value)}
                  className="resize-none"
                />
              </Field>

              {/* Linked assets — multi-angle gallery for Create-Ad auto-bind */}
              <Field label="Linked Images">
                <ProductAssetManager
                  product={product}
                  onChanged={(next) => {
                    setProduct(next)
                    // Keep the editable draft in sync so a subsequent Save
                    // call doesn't drop the asset link.
                    setDraft((d) => ({ ...d, assets: next.assets }))
                  }}
                />
              </Field>
            </div>

            <DialogFooter className="gap-2 flex-wrap">
              <Button
                variant="destructive"
                size="sm"
                onClick={remove}
                disabled={deleting}
                className="mr-auto"
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/create?product_id=${product.id}`)}
              >
                Create Ad with this product
              </Button>
              <Button size="sm" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// AddProductModal
// ---------------------------------------------------------------------------
function AddProductModal({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: () => void
}) {
  const [name, setName] = React.useState('')
  const [category, setCategory] = React.useState('')
  const [price, setPrice] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState('')

  const submit = async () => {
    if (!name.trim()) { setError('Name is required'); return }
    setSaving(true)
    setError('')
    try {
      await createProduct({ name: name.trim(), category: category || null, price: price || null, description: description || null })
      onCreated()
      onClose()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Product</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <Field label="Name *">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Vitamin C Serum" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Category">
              <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Skincare" />
            </Field>
            <Field label="Price">
              <Input value={price} onChange={(e) => setPrice(e.target.value)} placeholder="$49.99" />
            </Field>
          </div>
          <Field label="Description">
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className="resize-none" />
          </Field>
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={saving}>{saving ? 'Creating…' : 'Create'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// ScrapeModal
// ---------------------------------------------------------------------------
function ScrapeModal({
  onClose,
  onCreated,
}: {
  onClose: () => void
  onCreated: () => void
}) {
  const [url, setUrl] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState('')

  const submit = async () => {
    if (!url.trim()) { setError('URL is required'); return }
    setLoading(true)
    setError('')
    try {
      await scrapeProduct(url.trim())
      onCreated()
      onClose()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Scrape from URL</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">Paste a product page URL and we'll auto-populate name, description, benefits, ingredients, and more.</p>
          <Field label="Product URL">
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/products/serum"
              onKeyDown={(e) => e.key === 'Enter' && submit()}
            />
          </Field>
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={loading}>
            {loading ? 'Scraping…' : 'Scrape'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// ProductCard
// ---------------------------------------------------------------------------
function ProductCard({ product, onClick }: { product: ProductWithAssets; onClick: () => void }) {
  const benefitCount = product.benefits?.length ?? 0
  const heroAsset = product.assets?.find((pa) => pa.role === 'hero_image')?.asset
    ?? product.assets?.[0]?.asset

  return (
    <Card
      className="cursor-pointer hover:border-foreground/30 transition-colors group bg-card"
      onClick={onClick}
      role="button"
      aria-label={`Open ${product.name}`}
    >
      {/* Hero image */}
      <div className="h-40 rounded-t-lg overflow-hidden border-b border-border bg-accent/40">
        {heroAsset ? (
          <img
            src={`/uploads/${heroAsset.file_path}`}
            alt={heroAsset.original_filename ?? heroAsset.filename}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="h-10 w-10 text-muted-foreground/40" />
          </div>
        )}
      </div>

      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <p className="font-medium text-sm leading-tight line-clamp-2">{product.name}</p>
          <Pencil className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-0.5" />
        </div>

        <div className="flex flex-wrap gap-1.5">
          {product.price && (
            <Badge variant="outline" className="text-xs">{product.price}</Badge>
          )}
          {product.category && (
            <Badge variant="secondary" className="text-xs">{product.category}</Badge>
          )}
          {benefitCount > 0 && (
            <Badge variant="outline" className="text-xs text-muted-foreground">
              {benefitCount} benefit{benefitCount !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>

        {product.description && (
          <p className="text-xs text-muted-foreground line-clamp-2">{product.description}</p>
        )}
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Products page
// ---------------------------------------------------------------------------
export function Products() {
  const [products, setProducts] = React.useState<ProductWithAssets[]>([])
  const [loading, setLoading] = React.useState(true)
  const [selectedId, setSelectedId] = React.useState<number | null>(null)
  const [showAdd, setShowAdd] = React.useState(false)
  const [showScrape, setShowScrape] = React.useState(false)

  const load = React.useCallback(async () => {
    setLoading(true)
    try {
      setProducts(await fetchProducts())
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => { load() }, [load])

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0 gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-muted-foreground" />
          <h1 className="text-lg font-semibold">Products</h1>
          {!loading && (
            <Badge variant="outline" className="text-xs">{products.length}</Badge>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowScrape(true)}
            className="gap-1.5"
          >
            <Globe className="h-4 w-4" />
            Scrape from URL
          </Button>
          <Button
            size="sm"
            onClick={() => setShowAdd(true)}
            className="gap-1.5"
          >
            <Plus className="h-4 w-4" />
            Add Product
          </Button>
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-40 w-full rounded-lg" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center gap-4">
            <Package className="h-12 w-12 text-muted-foreground/30" />
            <div>
              <p className="text-sm font-medium">No products yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Scrape a product URL, or add manually.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap justify-center">
              <Button variant="outline" size="sm" onClick={() => setShowScrape(true)}>Scrape from URL</Button>
              <Button variant="outline" size="sm" onClick={() => setShowAdd(true)}>Add Product</Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} onClick={() => setSelectedId(p.id)} />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {selectedId !== null && (
        <ProductDetailModal
          productId={selectedId}
          onClose={() => setSelectedId(null)}
          onSaved={load}
        />
      )}
      {showAdd && (
        <AddProductModal onClose={() => setShowAdd(false)} onCreated={load} />
      )}
      {showScrape && (
        <ScrapeModal onClose={() => setShowScrape(false)} onCreated={load} />
      )}
    </div>
  )
}
